// Tabs of a kind's spec page, registered by the features that own them (ADR 0016): the spec page
// renders the tabs of its kind without knowing any kind.
import type { EnvironmentView, ObjectView, SpecRef } from "@cosmosai/api-client";
import type { ComponentType } from "react";

/** The spec page's own tabs; a registered tab never takes one of their ids. */
export const BUILT_IN_TABS = ["overview", "versions", "yaml", "diff"] as const;

/** What a registered tab receives: the spec page's context. */
export interface KindTabProps {
  workspace: string;
  specRef: SpecRef;
  spec: ObjectView;
  environments: EnvironmentView[];
  /** The environment selected in the top bar, if any. */
  environment: EnvironmentView | null;
}

export interface KindTab {
  /** The kind as written in a spec, such as "KnowledgeBase". */
  kind: string;
  /** The `tab` value in the URL: lowercase letters and dashes. */
  id: string;
  labelKey: string;
  order: number;
  panel: ComponentType<KindTabProps>;
}

const tabs: KindTab[] = [];
const ID = /^[a-z][a-z-]{0,30}$/;

export function registerKindTab(tab: KindTab): void {
  if (!ID.test(tab.id)) {
    throw new Error(`a kind tab id is lowercase letters and dashes, not "${tab.id}"`);
  }
  if ((BUILT_IN_TABS as readonly string[]).includes(tab.id)) {
    throw new Error(`a kind tab cannot take the built-in id "${tab.id}"`);
  }
  if (tabs.some((found) => found.kind === tab.kind && found.id === tab.id)) {
    throw new Error(`kind tab already registered: ${tab.kind}/${tab.id}`);
  }
  tabs.push(tab);
}

/** The tabs registered for `kind`, in their order. */
export const tabsOf = (kind: string): KindTab[] =>
  tabs.filter((tab) => tab.kind === kind).sort((a, b) => a.order - b.order);
